import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/field';

/**
 * The fallback door.
 *
 * Inside LINE nobody reaches this screen — LIFF signs them in before the router
 * mounts. It exists for local development against the API without a LIFF app,
 * and as the honest failure surface when the LIFF exchange breaks.
 */
export function SignIn({ liffError }: { liffError?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const result = await authClient.signIn.email({ email, password });
    setBusy(false);

    if (result.error) {
      setError(result.error.message ?? 'เข้าสู่ระบบไม่สำเร็จ');
      return;
    }

    // The gate re-probes /api/me on mount, so a reload is the simplest way to
    // hand control back to it with the new cookie in place.
    window.location.reload();
  };

  return (
    <div className="mx-auto grid min-h-full max-w-md place-content-center gap-4 p-6">
      <h1 className="text-center font-display text-subhead">Jotkin</h1>

      {liffError && (
        <Card className="border-warning/40 bg-warning/10">
          <p className="text-sm">เชื่อมต่อ LINE ไม่สำเร็จ: {liffError}</p>
        </Card>
      )}

      <Card>
        <form className="space-y-4" onSubmit={submit}>
          <CardTitle>เข้าสู่ระบบ</CardTitle>

          <Field label="อีเมล">
            {(props) => (
              <Input
                {...props}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label="รหัสผ่าน">
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          {error && <p className="text-sm text-danger">{error}</p>}

          <Button type="submit" size="lg" disabled={busy}>
            {busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
          </Button>
        </form>
      </Card>

      <p className="text-center text-xs text-ink-muted">
        ปกติแล้วให้เปิดหน้านี้จากแอป LINE เพื่อเข้าสู่ระบบอัตโนมัติ
      </p>
    </div>
  );
}
